import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { DollarSign, TrendingUp, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { currency } from "@/lib/utils2";

const COLORS = ["#ea580c", "#f97316", "#fb923c", "#fdba74", "#fed7aa"];

export default function CostOfOwnershipWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/cost-of-ownership");
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="bg-zinc-900 border border-zinc-800 rounded-sm h-80 animate-pulse" />;
  }

  const topItems = (data?.items || []).slice(0, 6);
  const hasData = topItems.length > 0 && (data.total_maintenance > 0 || data.total_purchase > 0);

  return (
    <section className="bg-zinc-900 border border-zinc-800 rounded-sm" data-testid="cost-of-ownership-widget">
      <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">// Bottom line</div>
          <h2 className="font-display font-bold text-xl tracking-tight flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-orange-500" /> Cost of ownership
          </h2>
        </div>
        <Link to="/app/reports" className="text-xs uppercase tracking-widest text-orange-500 hover:text-orange-400 inline-flex items-center gap-1">
          Full report <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      {!hasData ? (
        <div className="px-6 py-10 text-center">
          <TrendingUp className="w-10 h-10 mx-auto text-zinc-700 mb-3" />
          <p className="text-zinc-400 text-sm">Add equipment purchase prices and log maintenance costs — we'll chart your total investment per piece.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3">
          {/* Totals */}
          <div className="lg:col-span-1 p-5 lg:border-r border-zinc-800 space-y-4">
            <KPI label="Total invested" value={currency(data.total_invested)} tone="text-orange-500" big />
            <KPI label="Purchase cost" value={currency(data.total_purchase)} />
            <KPI label="Maintenance spend" value={currency(data.total_maintenance)} />
            <div className="text-[10px] uppercase tracking-widest text-zinc-600 pt-2 border-t border-zinc-800">
              Across {data.items.length} piece{data.items.length === 1 ? "" : "s"}
            </div>
          </div>

          {/* Chart */}
          <div className="lg:col-span-2 p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Top spenders</div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={topItems} margin={{ top: 5, right: 20, left: 0, bottom: 20 }}>
                <XAxis
                  dataKey="name"
                  stroke="#71717a"
                  tick={{ fontSize: 11, fill: "#a1a1aa" }}
                  interval={0}
                  angle={-20}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  stroke="#71717a"
                  tick={{ fontSize: 11, fill: "#a1a1aa" }}
                  tickFormatter={(v) => `$${v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}`}
                />
                <Tooltip
                  contentStyle={{ background: "#09090b", border: "1px solid #27272a", borderRadius: 2, fontSize: 12 }}
                  labelStyle={{ color: "#fafafa" }}
                  cursor={{ fill: "rgba(234, 88, 12, 0.08)" }}
                  formatter={(value, _, props) => [currency(value), props.payload.name]}
                />
                <Bar dataKey="total_invested" radius={[2, 2, 0, 0]}>
                  {topItems.map((entry, i) => (
                    <Cell key={entry.equipment_id || `cell-${i}`} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* Table */}
            <div className="mt-4 border-t border-zinc-800 pt-3 space-y-2">
              {topItems.map((it) => (
                <Link
                  key={it.equipment_id}
                  to={`/app/equipment/${it.equipment_id}`}
                  className="flex items-center justify-between text-xs hover:bg-zinc-800/40 px-2 py-1.5 rounded-sm transition-colors"
                  data-testid={`coo-row-${it.equipment_id}`}
                >
                  <div className="flex-1 min-w-0 truncate">
                    <span className="text-zinc-200">{it.name}</span>
                    <span className="text-zinc-600 mx-2">·</span>
                    <span className="text-zinc-500">{it.record_count} logs</span>
                  </div>
                  <div className="font-mono text-zinc-400 ml-3 shrink-0">
                    <span className="text-orange-500">{currency(it.total_invested)}</span>
                    <span className="text-zinc-700 text-[10px] ml-2">
                      {currency(it.maintenance_total)} mtc
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function KPI({ label, value, tone = "text-zinc-100", big = false }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className={`font-display font-black tracking-tighter ${big ? "text-4xl" : "text-2xl"} ${tone}`}>{value}</div>
    </div>
  );
}
